import { showToast } from '@/lib/utils/toast';
import { api } from '@/lib/api/client';

// Extend ImportMeta interface for Vite environment variables
interface ImportMetaEnv {
  DEV: boolean;
  PROD: boolean;
  SSR: boolean;
}

interface ImportMeta {
  env: ImportMetaEnv;
}

// Extended Performance API types for better type safety
declare global {
  interface PerformanceEntry {
    // First Input Delay entry properties
    processingStart?: number;
    // Layout Shift entry properties
    hadRecentInput?: boolean;
    value?: number;
  }
}

// Analytics event types
export interface AnalyticsEvent {
  event: string;
  properties?: Record<string, any>;
  timestamp: string;
  userId?: string;
  sessionId: string;
  userAgent: string;
  url: string;
  referrer?: string;
}

export interface PageViewEvent extends AnalyticsEvent {
  event: 'page_view';
  properties: {
    page: string;
    title: string;
    duration?: number;
    timeOnPage?: number;
  };
}

export interface StreamEvent extends AnalyticsEvent {
  event: 'stream_view' | 'stream_play' | 'stream_pause' | 'stream_end' | 'stream_quality_change';
  properties: {
    streamId: number;
    streamTitle: string;
    currentTime?: number;
    duration?: number;
    quality?: string;
    source: 'direct' | 'search' | 'recommendation' | 'notification';
  };
}

export interface UserInteractionEvent extends AnalyticsEvent {
  event: 'comment_post' | 'comment_like' | 'comment_reply' | 'user_follow' | 'notification_click';
  properties: {
    targetId?: number;
    targetType?: 'stream' | 'comment' | 'user';
    interactionType: string;
  };
}

export interface SearchEvent extends AnalyticsEvent {
  event: 'search_performed' | 'search_result_click';
  properties: {
    query: string;
    filters?: Record<string, any>;
    resultCount?: number;
    selectedResult?: number;
    searchType: 'streams' | 'users' | 'comments' | 'global';
  };
}

export interface ErrorEvent extends AnalyticsEvent {
  event: 'error_occurred';
  properties: {
    errorType: 'javascript' | 'network' | 'validation' | 'authentication';
    errorMessage: string;
    stackTrace?: string;
    component?: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
  };
}

export interface PerformanceEvent extends AnalyticsEvent {
  event: 'performance_metric';
  properties: {
    metric: string;
    value: number;
    unit: 'ms' | 'bytes' | 'score';
    threshold?: number;
  };
}

// Analytics tracker class
export class AnalyticsTracker {
  private static instance: AnalyticsTracker;
  private events: AnalyticsEvent[] = [];
  private sessionId: string;
  private userId?: string;
  private batchSize = 10;
  private flushInterval = 30000; // 30 seconds
  private isEnabled = true;
  private consecutiveFailures = 0;
  private maxConsecutiveFailures = 3;
  private isDisabled = false;
  private flushIntervalId: NodeJS.Timeout | null = null;
  private beforeUnloadHandler: (() => void) | null = null;

  private constructor() {
    this.sessionId = this.generateSessionId();

    // Disable analytics in development to prevent console spam
    if (import.meta.env.DEV) {
      console.log('[Analytics] Disabled in development mode');
      this.isDisabled = true;
      return;
    }

    this.startPeriodicFlush();
    this.trackPageViews();
    this.trackPerformance();
    this.trackErrors();
  }

  static getInstance(): AnalyticsTracker {
    if (!AnalyticsTracker.instance) {
      AnalyticsTracker.instance = new AnalyticsTracker();
    }
    return AnalyticsTracker.instance;
  }

  // Enable/disable analytics tracking
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  // Set user ID for authenticated users
  setUserId(userId: string): void {
    this.userId = userId;
  }

  // Clear user ID on logout
  clearUserId(): void {
    this.userId = undefined;
  }

  // Track page views automatically
  private trackPageViews(): void {
    if (typeof window === 'undefined') return;

    let startTime = Date.now();

    // Track initial page view
    this.trackEvent({
      event: 'page_view',
      properties: {
        page: window.location.pathname,
        title: document.title,
      },
    });

    // Track navigation changes
    const handleNavigation = () => {
      const duration = Date.now() - startTime;
      startTime = Date.now();

      this.trackEvent({
        event: 'page_view',
        properties: {
          page: window.location.pathname,
          title: document.title,
          duration,
        },
      });
    };

    // Listen for navigation events
    window.addEventListener('popstate', handleNavigation);

    // For SPA navigation (Next.js)
    const originalPushState = history.pushState;
    history.pushState = function (...args) {
      originalPushState.apply(this, args);
      handleNavigation();
    };
  }

  // Track performance metrics
  private trackPerformance(): void {
    if (typeof window === 'undefined') return;

    // Core Web Vitals
    if ('web-vitals' in window || 'PerformanceObserver' in window) {
      // Track LCP (Largest Contentful Paint)
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.trackEvent({
            event: 'performance_metric',
            properties: {
              metric: 'lcp',
              value: entry.startTime,
              unit: 'ms',
              threshold: 2500,
            },
          });
        }
      }).observe({ entryTypes: ['largest-contentful-paint'] });

      // Track FID (First Input Delay)
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.trackEvent({
            event: 'performance_metric',
            properties: {
              metric: 'fid',
              value: entry.processingStart! - entry.startTime,
              unit: 'ms',
              threshold: 100,
            },
          });
        }
      }).observe({ entryTypes: ['first-input'] });

      // Track CLS (Cumulative Layout Shift)
      new PerformanceObserver((list) => {
        let clsValue = 0;
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) {
            clsValue += entry.value || 0;
          }
        }
        this.trackEvent({
          event: 'performance_metric',
          properties: {
            metric: 'cls',
            value: clsValue,
            unit: 'score',
            threshold: 0.1,
          },
        });
      }).observe({ entryTypes: ['layout-shift'] });
    }

    // Track page load time
    window.addEventListener('load', () => {
      const loadTime = performance.now();
      this.trackEvent({
        event: 'performance_metric',
        properties: {
          metric: 'page_load_time',
          value: loadTime,
          unit: 'ms',
          threshold: 3000,
        },
      });
    });
  }

  // Track JavaScript errors
  private trackErrors(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('error', (event) => {
      this.trackEvent({
        event: 'error_occurred',
        properties: {
          errorType: 'javascript',
          errorMessage: event.message,
          stackTrace: event.error?.stack,
          component: 'global',
          severity: 'high',
        },
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.trackEvent({
        event: 'error_occurred',
        properties: {
          errorType: 'javascript',
          errorMessage: event.reason?.toString() || 'Unhandled promise rejection',
          severity: 'high',
        },
      });
    });
  }

  // Track custom events
  trackEvent(event: Omit<AnalyticsEvent, 'timestamp' | 'sessionId' | 'userId' | 'userAgent' | 'url' | 'referrer'>): void {
    if (!this.isEnabled || this.isDisabled) return;

    const fullEvent: AnalyticsEvent = {
      ...event,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: this.userId,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      url: typeof window !== 'undefined' ? window.location.href : '',
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    };

    this.events.push(fullEvent);

    // Flush if batch size reached
    if (this.events.length >= this.batchSize) {
      this.flush();
    }
  }

  // Specific tracking methods for common events
  trackPageView(page: string, title: string, duration?: number): void {
    this.trackEvent({
      event: 'page_view',
      properties: {
        page,
        title,
        duration,
      },
    });
  }

  trackStreamView(streamId: number, streamTitle: string, source: string = 'direct'): void {
    this.trackEvent({
      event: 'stream_view',
      properties: {
        streamId,
        streamTitle,
        source,
      },
    });
  }

  trackStreamPlay(streamId: number, streamTitle: string, currentTime: number = 0): void {
    this.trackEvent({
      event: 'stream_play',
      properties: {
        streamId,
        streamTitle,
        currentTime,
      },
    });
  }

  trackStreamPause(streamId: number, streamTitle: string, currentTime: number): void {
    this.trackEvent({
      event: 'stream_pause',
      properties: {
        streamId,
        streamTitle,
        currentTime,
      },
    });
  }

  trackStreamEnd(streamId: number, streamTitle: string, duration: number): void {
    this.trackEvent({
      event: 'stream_end',
      properties: {
        streamId,
        streamTitle,
        duration,
      },
    });
  }

  trackStreamViewerJoin(streamId: number): void {
    this.trackEvent({
      event: 'stream_viewer_join',
      properties: {
        streamId,
        count: 1,
      },
    });
  }

  trackStreamViewerLeave(streamId: number): void {
    this.trackEvent({
      event: 'stream_viewer_leave',
      properties: {
        streamId,
        count: -1,
      },
    });
  }

  trackStreamViewerCount(streamId: number, count: number): void {
    this.trackEvent({
      event: 'stream_viewer_count',
      properties: {
        streamId,
        count,
      },
    });
  }

  trackApiLatency(endpoint: string, latencyMs: number): void {
    this.trackEvent({
      event: 'api_latency',
      properties: {
        metric: 'api_latency',
        value: latencyMs,
        unit: 'ms',
        endpoint,
        threshold: 1000,
      },
    });
  }

  trackPlayerStartup(streamId: number, startupTimeMs: number): void {
    this.trackEvent({
      event: 'player_startup',
      properties: {
        metric: 'player_startup',
        value: startupTimeMs,
        unit: 'ms',
        streamId,
        threshold: 3000,
      },
    });
  }

  trackCommentPost(streamId: number, commentId: number): void {
    this.trackEvent({
      event: 'comment_post',
      properties: {
        targetId: streamId,
        targetType: 'stream',
        interactionType: 'comment',
      },
    });
  }

  trackCommentLike(commentId: number): void {
    this.trackEvent({
      event: 'comment_like',
      properties: {
        targetId: commentId,
        targetType: 'comment',
        interactionType: 'like',
      },
    });
  }

  trackCommentReport(commentId: number, reason?: string): void {
    this.trackEvent({
      event: 'comment_report',
      properties: {
        targetId: commentId,
        targetType: 'comment',
        interactionType: 'report',
        reason,
      },
    });
  }

  trackLikeAdd(targetId: number, targetType: string): void {
    this.trackEvent({
      event: 'like_add',
      properties: {
        targetId,
        targetType,
        interactionType: 'like',
      },
    });
  }

  trackReactionAdd(targetId: number, targetType: string, reactionType: string): void {
    this.trackEvent({
      event: 'reaction_add',
      properties: {
        targetId,
        targetType,
        interactionType: 'reaction',
        reactionType,
      },
    });
  }

  trackShare(targetId: number, targetType: string, platform?: string): void {
    this.trackEvent({
      event: 'share',
      properties: {
        targetId,
        targetType,
        interactionType: 'share',
        platform,
      },
    });
  }

  trackSearch(query: string, resultCount: number, searchType: string = 'global'): void {
    this.trackEvent({
      event: 'search_performed',
      properties: {
        query,
        resultCount,
        searchType,
      },
    });
  }

  trackSearchResultClick(query: string, selectedResult: number): void {
    this.trackEvent({
      event: 'search_result_click',
      properties: {
        query,
        selectedResult,
      },
    });
  }

  trackError(errorType: string, errorMessage: string, severity: string = 'medium', component?: string): void {
    this.trackEvent({
      event: 'error_occurred',
      properties: {
        errorType,
        errorMessage,
        component,
        severity,
      },
    });
  }

  // Flush events to server
  private async flush(): Promise<void> {
    if (this.events.length === 0 || this.isDisabled) return;

    const eventsToSend = [...this.events];
    this.events = []; // Clear the queue

    try {
      // Send to analytics endpoint
      const response = await api.post('api/analytics', { events: eventsToSend });

      if (!response.success) {
        throw new Error(response.error?.message || 'Analytics API error');
      }

      // Reset failure count on success
      this.consecutiveFailures = 0;
    } catch (error) {
      console.error('Failed to send analytics events:', error);
      this.consecutiveFailures++;

      // Re-queue events for retry
      if (Array.isArray(eventsToSend)) {
        this.events.unshift(...eventsToSend);
      }

      // Disable analytics after too many consecutive failures
      if (this.consecutiveFailures >= this.maxConsecutiveFailures) {
        console.warn('Analytics disabled due to repeated failures');
        this.isDisabled = true;

        // Show error only in development
        if (import.meta.env.DEV) {
          showToast.error('Analytics tracking disabled due to server issues');
        }
      } else {
        // Show error only in development and not too frequently
        if (import.meta.env.DEV && this.consecutiveFailures === 1) {
          showToast.error('Analytics tracking failed - will retry');
        }
      }
    }
  }

  // Start periodic flush
  private startPeriodicFlush(): void {
    if (typeof window === 'undefined') return;

    // Store interval ID for cleanup
    this.flushIntervalId = setInterval(() => {
      this.flush();
    }, this.flushInterval);

    // Store handler reference for cleanup
    this.beforeUnloadHandler = () => {
      this.flush();
    };

    // Flush on page unload
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  // Stop periodic flush and cleanup listeners
  private stopPeriodicFlush(): void {
    if (this.flushIntervalId !== null) {
      clearInterval(this.flushIntervalId);
      this.flushIntervalId = null;
    }

    if (this.beforeUnloadHandler !== null && typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }

  // Generate unique session ID
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Get current session data
  getSessionData(): { sessionId: string; userId?: string; eventCount: number } {
    return {
      sessionId: this.sessionId,
      userId: this.userId,
      eventCount: this.events.length,
    };
  }

  // Force flush (useful for testing)
  forceFlush(): Promise<void> {
    return this.flush();
  }
}

// Export singleton instance
export const analytics = AnalyticsTracker.getInstance();

// Helper hook for React components
export function useAnalytics() {
  return {
    trackEvent: analytics.trackEvent.bind(analytics),
    trackPageView: analytics.trackPageView.bind(analytics),
    trackStreamView: analytics.trackStreamView.bind(analytics),
    trackStreamPlay: analytics.trackStreamPlay.bind(analytics),
    trackStreamPause: analytics.trackStreamPause.bind(analytics),
    trackStreamEnd: analytics.trackStreamEnd.bind(analytics),
    trackStreamViewerJoin: analytics.trackStreamViewerJoin.bind(analytics),
    trackStreamViewerLeave: analytics.trackStreamViewerLeave.bind(analytics),
    trackStreamViewerCount: analytics.trackStreamViewerCount.bind(analytics),
    trackApiLatency: analytics.trackApiLatency.bind(analytics),
    trackPlayerStartup: analytics.trackPlayerStartup.bind(analytics),
    trackCommentPost: analytics.trackCommentPost.bind(analytics),
    trackCommentLike: analytics.trackCommentLike.bind(analytics),
    trackCommentReport: analytics.trackCommentReport.bind(analytics),
    trackLikeAdd: analytics.trackLikeAdd.bind(analytics),
    trackReactionAdd: analytics.trackReactionAdd.bind(analytics),
    trackShare: analytics.trackShare.bind(analytics),
    trackSearch: analytics.trackSearch.bind(analytics),
    trackSearchResultClick: analytics.trackSearchResultClick.bind(analytics),
    trackError: analytics.trackError.bind(analytics),
    getSessionData: analytics.getSessionData.bind(analytics),
  };
}
